import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Navigation, Phone, Shield, Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MerchantFinder = ({ open, onClose }) => {
  const [searchType, setSearchType] = useState('location'); // 'location', 'province', 'address'
  const [province, setProvince] = useState('');
  const [searchAddress, setSearchAddress] = useState('');
  const [maxDistance, setMaxDistance] = useState(10);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: -26.2041, lng: 28.0473 }); // Johannesburg
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);

  const provinces = [
    'Gauteng',
    'Western Cape',
    'KwaZulu-Natal',
    'Eastern Cape',
    'Free State',
    'Limpopo',
    'Mpumalanga',
    'Northern Cape',
    'North West'
  ];

  // Initialize Google Map. The dialog content unmounts on close, destroying
  // the map's DOM node — so reset the map ref on close and re-create on open.
  // Polls briefly in case the Maps script hasn't finished loading yet.
  useEffect(() => {
    if (!open) {
      googleMapRef.current = null;
      markersRef.current = [];
      return;
    }
    let cancelled = false;
    const tryInit = (attempt = 0) => {
      if (cancelled || googleMapRef.current) return;
      if (mapRef.current && window.google) {
        googleMapRef.current = new window.google.maps.Map(mapRef.current, {
          center: mapCenter,
          zoom: 12,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });
      } else if (attempt < 20) {
        setTimeout(() => tryInit(attempt + 1), 300);
      }
    };
    tryInit();
    return () => { cancelled = true; };
  }, [open]);

  // Update map markers when merchants change
  useEffect(() => {
    if (googleMapRef.current && merchants.length > 0) {
      // Clear existing markers
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];

      // Add new markers
      const bounds = new window.google.maps.LatLngBounds();
      
      merchants.forEach((merchant, index) => {
        if (merchant.location && merchant.location.latitude && merchant.location.longitude) {
          const position = {
            lat: merchant.location.latitude,
            lng: merchant.location.longitude
          };

          const marker = new window.google.maps.Marker({
            position,
            map: googleMapRef.current,
            title: merchant.business_name,
            label: {
              text: `${index + 1}`,
              color: 'white',
              fontWeight: 'bold'
            },
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#F2B10C',
              fillOpacity: 1,
              strokeColor: '#1B0C34',
              strokeWeight: 2
            }
          });

          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="padding: 8px;">
                <h3 style="margin: 0 0 8px 0; font-weight: bold; color: #1B0C34;">${merchant.business_name}</h3>
                ${merchant.location.address ? `<p style="margin: 4px 0; font-size: 14px;">${merchant.location.address}</p>` : ''}
                ${merchant.distance_km != null ? `<p style="margin: 4px 0; color: #145539; font-weight: 600;">${merchant.distance_km} km away</p>` : ''}
              </div>
            `
          });

          marker.addListener('click', () => {
            infoWindow.open(googleMapRef.current, marker);
          });

          markersRef.current.push(marker);
          bounds.extend(position);
        }
      });

      if (merchants.length > 0) {
        googleMapRef.current.fitBounds(bounds);
      }
    }
  }, [merchants]);

  const getUserLocation = () => {
    setLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setUserLocation(location);
          setMapCenter({ lat: location.latitude, lng: location.longitude });
          if (googleMapRef.current) {
            googleMapRef.current.setCenter({ lat: location.latitude, lng: location.longitude });
          }
          searchNearbyMerchants(location.latitude, location.longitude);
        },
        (error) => {
          toast.error('Unable to get your location. Please check permissions.');
          setLoading(false);
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
      setLoading(false);
    }
  };

  const searchNearbyMerchants = async (lat, lng, maxDist = maxDistance) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/merchants/search`, {
        params: {
          latitude: lat,
          longitude: lng,
          max_distance_km: maxDist
        }
      });

      if (response.data.success) {
        setMerchants(response.data.merchants);
        if (response.data.merchants.length === 0) {
          toast.info('No merchants found in your area');
        } else {
          toast.success(`Found ${response.data.merchants.length} merchants`);
        }
      }
    } catch (error) {
      toast.error('Failed to search merchants');
    } finally {
      setLoading(false);
    }
  };

  const searchByProvince = async () => {
    if (!province) {
      toast.error('Please select a province');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/merchants/search`, {
        params: { province, limit: 50 }
      });

      if (response.data.success) {
        setMerchants(response.data.merchants);
        toast.success(`Found ${response.data.merchants.length} merchants in ${province}`);
      }
    } catch (error) {
      toast.error('Failed to search merchants');
    } finally {
      setLoading(false);
    }
  };

  const searchByAddress = async () => {
    if (!searchAddress) {
      toast.error('Please enter an address or business name');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/merchants/search`, {
        params: { address: searchAddress, limit: 50 }
      });

      if (response.data.success) {
        setMerchants(response.data.merchants);
        if (response.data.merchants.length === 0) {
          toast.info('No merchants found matching your search');
        } else {
          toast.success(`Found ${response.data.merchants.length} merchants`);
        }
      }
    } catch (error) {
      toast.error('Failed to search merchants');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchType === 'location') {
      if (userLocation) {
        searchNearbyMerchants(userLocation.latitude, userLocation.longitude);
      } else {
        getUserLocation();
      }
    } else if (searchType === 'province') {
      searchByProvince();
    } else if (searchType === 'address') {
      searchByAddress();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34] flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Find Nearby Mojo Money Merchants
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex gap-4 overflow-hidden">
          {/* Left Panel - Search & Results */}
          <div className="w-1/3 flex flex-col gap-4 overflow-hidden">
            {/* Search Controls */}
            <Card>
              <CardContent className="p-4 space-y-4">
                <div>
                  <Label>Search By</Label>
                  <Select value={searchType} onValueChange={setSearchType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="location">My Location</SelectItem>
                      <SelectItem value="province">Province</SelectItem>
                      <SelectItem value="address">Address/Name</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {searchType === 'location' && (
                  <div>
                    <Label>Max Distance (km)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={maxDistance}
                      onChange={(e) => setMaxDistance(e.target.value)}
                    />
                  </div>
                )}

                {searchType === 'province' && (
                  <div>
                    <Label>Province</Label>
                    <Select value={province} onValueChange={setProvince}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select province" />
                      </SelectTrigger>
                      <SelectContent>
                        {provinces.map(p => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {searchType === 'address' && (
                  <div>
                    <Label>Business Name or Address</Label>
                    <Input
                      placeholder="Search..."
                      value={searchAddress}
                      onChange={(e) => setSearchAddress(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>
                )}

                <Button
                  onClick={handleSearch}
                  disabled={loading}
                  className="w-full bg-[#145539] hover:bg-[#1e7049] text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Search Merchants
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Results List */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {merchants.length === 0 && !loading && (
                <p className="text-center text-gray-500 mt-8">
                  No merchants found. Try searching!
                </p>
              )}
              
              {merchants.map((merchant, index) => (
                <Card key={merchant.merchant_id} className="cursor-pointer hover:shadow-md transition-shadow">
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#F2B10C] flex items-center justify-center text-[#1B0C34] font-bold flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-[#1B0C34] truncate">
                            {merchant.business_name}
                          </h4>
                          {merchant.verified && (
                            <Shield className="w-4 h-4 text-green-600 flex-shrink-0" />
                          )}
                        </div>
                        {merchant.distance_km != null && (
                          <p className="text-sm text-[#145539] font-medium">
                            {merchant.distance_km} km away
                          </p>
                        )}
                        {merchant.location?.address && (
                          <p className="text-xs text-gray-600 truncate">
                            {merchant.location.address}
                          </p>
                        )}
                        {merchant.province && (
                          <p className="text-xs text-gray-500">
                            {merchant.province}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Right Panel - Map */}
          <div className="flex-1 rounded-lg overflow-hidden border border-gray-200">
            <div ref={mapRef} className="w-full h-full" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MerchantFinder;
